import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { EventContentArg } from '@fullcalendar/core';
import ukLocale from '@fullcalendar/core/locales/uk';
import enGbLocale from '@fullcalendar/core/locales/en-gb';
import plLocale from '@fullcalendar/core/locales/pl';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import FullCalendar from '@fullcalendar/react';
import SpinnerBlock from 'components/others/SpinnerBlock';
import ESpinner from 'components/others/Spinner';
import { getLocale } from 'utils/helpers/functions/getLocale';
import NewTask from '../NewTask';
import TaskCard from '../TaskCard';
import TaskCalendarCreateMenu from './TaskCalendarCreateMenu';
import TaskCalendarEventContent from './TaskCalendarEventContent';
import TaskCalendarToolbar from './TaskCalendarToolbar';
import useTaskCalendar, { type TaskCalendarView } from './model/useTaskCalendar';

const CALENDAR_PLUGINS = [dayGridPlugin, timeGridPlugin, interactionPlugin];

const resolveFcLocale = (language: string) => {
    const code = getLocale(language);
    if (code.startsWith('en')) return enGbLocale;
    if (code.startsWith('pl')) return plLocale;
    return ukLocale;
};

const TaskCalendar = () => {
    const { t, i18n } = useTranslation(['Tasks']);
    const calendarRef = useRef<FullCalendar>(null);
    const calendar = useTaskCalendar();
    const { setView } = calendar;
    const executorLabel = t('fields.executor');
    const controllerLabel = t('fields.controller');

    const goPrev = useCallback(() => {
        calendarRef.current?.getApi().prev();
    }, []);

    const goNext = useCallback(() => {
        calendarRef.current?.getApi().next();
    }, []);

    const goToday = useCallback(() => {
        calendarRef.current?.getApi().today();
    }, []);

    const goToDate = useCallback((date: Date) => {
        calendarRef.current?.getApi().gotoDate(date);
    }, []);

    const handleViewChange = useCallback((next: TaskCalendarView) => {
        setView(next);
        calendarRef.current?.getApi().changeView(next);
    }, [setView]);

    const renderEventContent = useCallback((arg: EventContentArg) => (
        <TaskCalendarEventContent
            arg={arg}
            executorLabel={executorLabel}
            controllerLabel={controllerLabel}
        />
    ), [executorLabel, controllerLabel]);

    if (calendar.tasks === null) {
        return (
            <div className="py-5">
                <ESpinner />
            </div>
        );
    }

    return (
        <>
            <SpinnerBlock onlyBlur={true} show={calendar.showSpinner || calendar.dateUpdating} />
            <div
                className={
                    calendar.canCreate
                        ? 'task-calendar task-calendar--can-create position-relative'
                        : 'task-calendar position-relative'
                }
            >
                <TaskCalendarToolbar
                    view={calendar.view}
                    title={calendar.viewTitle}
                    currentDate={calendar.currentDate}
                    onViewChange={handleViewChange}
                    onPrev={goPrev}
                    onNext={goNext}
                    onToday={goToday}
                    onGoToDate={goToDate}
                />
                <div className="task-calendar__grid">
                    <div
                        className={
                            calendar.view === 'timeGridDay'
                                ? 'task-calendar__canvas'
                                : 'task-calendar__canvas task-calendar__canvas--wide'
                        }
                    >
                        <FullCalendar
                            ref={calendarRef}
                            plugins={CALENDAR_PLUGINS}
                            initialView={calendar.view}
                            headerToolbar={false}
                            events={calendar.events}
                            locale={resolveFcLocale(i18n.language)}
                            height="100%"
                            editable={true}
                            eventStartEditable={true}
                            eventDurationEditable={true}
                            eventResizableFromStart={true}
                            selectable={false}
                            dayMaxEvents={3}
                            eventDragMinDistance={8}
                            eventContent={renderEventContent}
                            eventClick={calendar.handleEventClick}
                            eventAllow={calendar.handleEventAllow}
                            eventDrop={calendar.handleEventDrop}
                            eventResize={calendar.handleEventResize}
                            eventDragStart={calendar.hideCreateMenu}
                            dateClick={calendar.handleDateClick}
                            datesSet={calendar.handleDatesSet}
                        />
                    </div>
                </div>
            </div>
            {calendar.createMenu ? (
                <TaskCalendarCreateMenu
                    x={calendar.createMenu.x}
                    y={calendar.createMenu.y}
                    dateMillis={calendar.createMenu.dateMillis}
                    allDay={calendar.createMenu.allDay}
                    onCreate={calendar.openNewTaskFromMenu}
                    onHide={calendar.hideCreateMenu}
                />
            ) : null}
            {calendar.canCreate ? (
                <NewTask
                    show={calendar.showNewTask}
                    defaultDue={calendar.defaultDue}
                    onHide={calendar.hideNewTask}
                    onCreated={calendar.handleTaskCreated}
                />
            ) : null}
            <TaskCard taskId={calendar.openTaskId} onHide={calendar.hideTaskCard} />
        </>
    );
};

export default TaskCalendar;
